import { supabase } from "@/integrations/supabase/client";
import { UserProfile } from "@/types";
import { toast } from "sonner";

;
import { throttledSupabaseRequest } from "@/utils/supabaseHelpers";

export const useAuthBasicActions = (
  setUser: React.Dispatch<React.SetStateAction<unknown>>,
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>,
  setIsLoggingOut: React.Dispatch<React.SetStateAction<boolean>>
) => {
  // Track the current user data
  let currentUser: UserProfile | null = null;
  
  const getCurrentUser = (): UserProfile | null => {
    return currentUser;
  };
  
  const setCurrentUser = (user: UserProfile | null) => {
    currentUser = user;
    setUser(user);
  };

  const logout = async (): Promise<void> => {
    setIsLoggingOut(true);
    try {
      // Clear local state first to ensure UI updates immediately
      setCurrentUser(null);
      setIsAuthenticated(false);

      // Attempt to sign out from Supabase
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        
        // Only show error toast if it's not a "session not found" error
        if (!error.message.includes('session not found')) {
          toast.error('Failed to log out', {
            id: 'logout-error'
          });
          return;
        }
      }
      
      // Navigate to login with state indicating logout
      toast.success('Logged out successfully', {
        id: 'logout-success'
      });

      // Add history.replaceState to set isLogout state
      window.history.replaceState({ isLogout: true }, document.title, window.location.pathname);
    } catch {
      // Don't show error toast since we've already cleared local state
    } finally {
      setIsLoggingOut(false);
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      
      const signIn = async (email: string, password: string) => {
        try {
          // Use throttled request to prevent excessive API calls
          await throttledSupabaseRequest(
            `auth:signIn:${email}`,
            async () => {
              const { error } = await supabase.auth.signInWithPassword({
                email,
                password,
              });

              if (error) throw error;
              return true;
            }
          );

          return { success: true };
        } catch (signInErr) {
          const message = signInErr instanceof Error ? signInErr.message : 'Failed to sign in';
          toast.error(message);
          return { success: false, error: signInErr };
        }
      };
      const result = await signIn(email, password);
      if (result.success) {
        return true;
      } else {
        return false;
      }
    } catch {
      toast.error('An unexpected error occurred during login', {
        id: `login-exception-${email}`
      });
      return false;
    }
  };
  
  const register = async (email: string, password: string, name: string): Promise<void> => {
    try {
      const signUp = async (email: string, password: string, metadata: Record<string, unknown>) => {
        try {
          // Use throttled request to prevent excessive API calls
          await throttledSupabaseRequest(
            `auth:signUp:${email}`,
            async () => {
              const { error } = await supabase.auth.signUp({
                email,
                password,
                options: {
                  data: metadata,
                },
              });

              if (error) throw error;
              return true;
            }
          );

          toast.success('Sign up successful! Please check your email to confirm your account.');
          return { success: true };
        } catch (signUpErr) {
          const message = signUpErr instanceof Error ? signUpErr.message : 'Failed to sign up';
          toast.error(message);
          return { success: false, error: signUpErr };
        }
      };
      const metadata = {
        name,
        first_name: name.split(' ')[0],
        last_name: name.split(' ').slice(1).join(' '),
      };
      const result = await signUp(email, password, metadata);
      if (!result.success) {
        throw result.error;
      }
    } catch {
      toast.error('An unexpected error occurred during registration', {
        id: `register-exception-${email}`
      });
      throw new Error('Registration failed');
    }
  };

  return {
    login,
    register,
    logout,
    getCurrentUser,
    setCurrentUser
  };
};
